export const CANCELLATION_BUSINESS_TIMEZONE = 'America/New_York';

/** Forward window size shared by the cancellation + default addition policies. */
export const CANCELLATION_FORWARD_MONTHS = 6;

export interface YearMonth {
  year: number;
  month: number;
}

export interface EasternDateParts extends YearMonth {
  day: number;
}

export function getEasternToday(now: Date = new Date()): EasternDateParts {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: CANCELLATION_BUSINESS_TIMEZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  });
  const parts = dtf.formatToParts(now);
  const y = parts.find((p) => p.type === 'year')?.value;
  const m = parts.find((p) => p.type === 'month')?.value;
  const d = parts.find((p) => p.type === 'day')?.value;

  return { year: Number(y), month: Number(m), day: Number(d) };
}

export function toMonthIndex(ym: YearMonth): number {
  return ym.year * 12 + ym.month;
}

export function fromMonthIndex(index: number): YearMonth {
  const zeroBased = index - 1;
  return {
    year: Math.floor(zeroBased / 12),
    month: (zeroBased % 12) + 1
  };
}

// ── Anchor: which calendar day a month selection resolves to ─────────────────

/** `'first'` → 1st of the month (additions); `'last'` → last day (cancellations). */
export type MonthAnchor = 'first' | 'last';

export function resolveAnchorDay(year: number, month: number, anchor: MonthAnchor): number {
  if (anchor === 'first') {
    return 1;
  }
  // Day 0 of the next (1-based) month === last day of this month.
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** "MMM D, YYYY" for the anchored day — e.g. "Jul 1, 2026" or "Jul 31, 2026". */
export function formatAnchoredMonthDisplay(year: number, month: number, anchor: MonthAnchor): string {
  const day = resolveAnchorDay(year, month, anchor);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });
}

/** ISO `yyyy-MM-dd` for the anchored day — handy when an API wants a full effective date. */
export function resolveAnchoredIsoDate(year: number, month: number, anchor: MonthAnchor): string {
  const day = resolveAnchorDay(year, month, anchor);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// ── Window: the inclusive [min, max] range a picker allows ───────────────────

export interface MonthWindow {
  min: YearMonth;
  max: YearMonth;
}

/**
 * Strategy for computing a picker's allowed month window.
 * Implement/assign a new one to change a flow's window without touching the
 * shared component (Open/Closed).
 */
export interface MonthWindowPolicy {
  getWindow(now?: Date): MonthWindow;
}

/** Window relative to the current Eastern month: [current − before, current + after]. */
export class RollingMonthWindowPolicy implements MonthWindowPolicy {
  constructor(
    private readonly monthsBefore: number,
    private readonly monthsAfter: number
  ) {}

  getWindow(now: Date = new Date()): MonthWindow {
    const today = getEasternToday(now);
    const current = toMonthIndex({ year: today.year, month: today.month });
    return {
      min: fromMonthIndex(current - this.monthsBefore),
      max: fromMonthIndex(current + this.monthsAfter)
    };
  }
}

export function isMonthInWindow(ym: YearMonth, window: MonthWindow): boolean {
  if (!Number.isInteger(ym.year) || !Number.isInteger(ym.month) || ym.month < 1 || ym.month > 12) {
    return false;
  }
  const idx = toMonthIndex(ym);
  return idx >= toMonthIndex(window.min) && idx <= toMonthIndex(window.max);
}

// ── Options: single-select rows rendered as full dates ───────────────────────

export interface MonthDateOption {
  /** Stable form value, e.g. `"2026-07"`. */
  value: string;
  year: number;
  month: number;
  /** Anchored full-date label, e.g. `"Jul 1, 2026"` or `"Jul 31, 2026"`. */
  label: string;
}

export function toMonthValue(ym: YearMonth): string {
  return `${ym.year}-${String(ym.month).padStart(2, '0')}`;
}

export function parseMonthValue(value: string | null | undefined): YearMonth | null {
  if (value == null || value === '') {
    return null;
  }
  const match = /^(\d{4})-(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return null;
  }
  return { year, month };
}

/** Build every selectable row for a window + anchor (previous → …), labeled as full dates. */
export function buildMonthDateOptions(window: MonthWindow, anchor: MonthAnchor): MonthDateOption[] {
  const options: MonthDateOption[] = [];
  for (let idx = toMonthIndex(window.min); idx <= toMonthIndex(window.max); idx++) {
    const ym = fromMonthIndex(idx);
    options.push({
      value: toMonthValue(ym),
      year: ym.year,
      month: ym.month,
      label: formatAnchoredMonthDisplay(ym.year, ym.month, anchor)
    });
  }
  return options;
}

// ── Shared policy instances ──────────────────────────────────────────────────

export const CANCELLATION_MONTHS_BEFORE = 1;
export const CANCELLATION_MONTHS_AFTER = CANCELLATION_FORWARD_MONTHS;

/** Cancellations: previous calendar month → current + 6 (last day of month). */
export const cancellationWindowPolicy: MonthWindowPolicy = new RollingMonthWindowPolicy(
  CANCELLATION_MONTHS_BEFORE,
  CANCELLATION_MONTHS_AFTER
);

/**
 * Additions: current month → current + 6 (first day of month).
 * NOTE: additions have no backdating window today — this is the newly-defined default.
 * Confirm with product; swap for a different policy here if the rule differs.
 */
export const additionWindowPolicy: MonthWindowPolicy = new RollingMonthWindowPolicy(
  0,
  CANCELLATION_FORWARD_MONTHS
);

/**
 * Open enrollment acknowledge window: month of the OE period start through
 * the OE/primary effective month + ForwardMonths (first of month).
 * Does not change additionWindowPolicy / cancellationWindowPolicy.
 */
export class OpenEnrollmentMonthWindowPolicy implements MonthWindowPolicy {
  constructor(
    private readonly periodStart: YearMonth,
    private readonly effective: YearMonth,
    private readonly monthsAfter: number = CANCELLATION_FORWARD_MONTHS
  ) {}

  getWindow(_now?: Date): MonthWindow {
    const minIdx = toMonthIndex(this.periodStart);
    const maxIdx = toMonthIndex(this.effective) + this.monthsAfter;
    const lo = Math.min(minIdx, maxIdx);
    const hi = Math.max(minIdx, maxIdx);
    return {
      min: fromMonthIndex(lo),
      max: fromMonthIndex(hi)
    };
  }
}

export function tryCreateOpenEnrollmentWindowPolicy(
  periodStartIso: string | null | undefined,
  effectiveIso: string | null | undefined
): OpenEnrollmentMonthWindowPolicy | null {
  const start = parseIsoToYearMonth(periodStartIso);
  const effective = parseIsoToYearMonth(effectiveIso);
  if (!start || !effective) {
    return null;
  }
  return new OpenEnrollmentMonthWindowPolicy(start, effective);
}

// ── Invoice-period eligibility: is a given invoice editable (last or current month)? ──

/**
 * Calendar year/month from an API date string (`yyyy-MM-dd` or `yyyy-MM-ddTHH:mm:ss…`).
 * Uses the date prefix only — same as queue `isoYmdToLocalDate` / store normalize.
 */
export function parseIsoToYearMonth(iso: string | null | undefined): YearMonth | null {
  if (!iso?.trim()) {
    return null;
  }
  const s = iso.trim();
  const ymd = /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : null;
  if (!ymd) {
    return null;
  }
  const [year, month] = ymd.split('-').map(Number);
  if (!year || month < 1 || month > 12) {
    return null;
  }
  return { year, month };
}

/**
 * True when `ym` is last calendar month OR the current calendar month relative
 * to `now` (Eastern time, matching the rest of the cancellation-window logic
 * above). Older invoices (2+ months back) and future-dated invoices are not
 * editable.
 */
export function isYearMonthLastOrCurrentCalendarMonth(ym: YearMonth | null, now: Date = new Date()): boolean {
  if (!ym) {
    return false;
  }
  const today = getEasternToday(now);
  const currentIndex = toMonthIndex({ year: today.year, month: today.month });
  const ymIndex = toMonthIndex(ym);
  return ymIndex === currentIndex || ymIndex === currentIndex - 1;
}
